import { useMemo, useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { useAuth } from "context/AuthContext";
import http from "services/httpClient";
import { getTimezoneOptions } from "utils/timezones";
import { DateTime } from "luxon";

interface EditProfileModalProps {
  show: boolean;
  onHide: () => void;
}

export const EditProfileModal: React.FC<EditProfileModalProps> = ({ show, onHide }) => {
  const { user} = useAuth();

  const timezones = useMemo(() => getTimezoneOptions(DateTime.now()), []);

  const [formData, setFormData] = useState({
    email: user?.email || "",
    first_name: "",
    last_name: "",
    cellphone: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // const timezones = [
  //   { label: "UTC", value: "UTC" },
  //   { label: "Asia/Manila (GMT+08:00)", value: "Asia/Manila" },
  //   { label: "Australia/Sydney (GMT+10:00)", value: "Australia/Sydney" },
  //   { label: "Australia/Brisbane (GMT+10:00)", value: "Australia/Brisbane" },
  //   { label: "Australia/Perth (GMT+08:00)", value: "Australia/Perth" },
  //   { label: "Australia/Adelaide (GMT+09:30)", value: "Australia/Adelaide" },
  // ];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await http.get("/user/profile/", {});
        setFormData({
          email: user?.email || "",
          first_name: res.data.first_name ?? "",
          last_name: res.data.last_name ?? "",
          cellphone: res.data.cellphone ?? "",
          timezone: res.data.timezone ?? "UTC",
        });
      } catch (err) {
        // console.error("Failed to fetch profile", err);
      }
    };

    if (show) fetchProfile();
  }, [show, user]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      await http.put(
        "/user/profile/",
        {
          first_name: formData.first_name,
          last_name: formData.last_name,
          cellphone: formData.cellphone,
          timezone: formData.timezone,
        }
      );
      setSuccess("Profile updated successfully.");
      setTimeout(() => onHide(), 1000);
    } catch (err) {
      setError("Failed to update profile.");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control type="email" value={formData.email} disabled />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phone Number</Form.Label>
          <Form.Control
            type="tel"
            name="cellphone"
            value={formData.cellphone}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Select name="timezone" value={formData.timezone} onChange={handleChange}>
          {timezones.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </Form.Select>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditProfileModal;